import React, { useState, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, EmptyState, SectionTitle, fmtHours } from "../components/common";
import { Layers, ArrowRight, GitCompare } from "lucide-react";

function Flow({ activities, base }) {
  const baseSet = base ? new Set(base) : null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {activities.map((a, i) => {
        const isNew = baseSet && !baseSet.has(a);
        return (
          <React.Fragment key={i}>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${isNew ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-700"}`}>{a}</span>
            {i < activities.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Inner() {
  const { datasetId } = useApp();
  const { data, loading } = useFetch(datasetId ? `/datasets/${datasetId}/variants` : null, { skip: !datasetId });
  const [sel, setSel] = useState(null);
  const happy = data?.[0]?.activities;
  const selected = useMemo(() => data?.find((v) => v.id === sel) || data?.[0], [data, sel]);

  if (!datasetId) return <EmptyState icon={Layers} title="No dataset selected" />;
  if (loading) return <Loading label="Clustering process variants…" />;
  if (!data) return null;

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Variant Analysis" title="Variant Explorer" sub={`${data.length} distinct execution paths discovered`} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <GlassCard className="lg:col-span-5 p-5">
          <h3 className="font-bold text-slate-900 mb-3">Variants by frequency</h3>
          <div className="space-y-2 max-h-[560px] overflow-y-auto no-scrollbar">
            {data.map((v, i) => (
              <button key={v.id} onClick={() => setSel(v.id)} data-testid={`variant-${v.id}`}
                className={`w-full text-left p-3 rounded-xl border pm-transition ${selected?.id === v.id ? "border-cyan-400 bg-cyan-50/60" : "border-white/60 bg-white/50 hover:bg-white/80"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold text-slate-800">Variant {i + 1} {i === 0 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-1">HAPPY PATH</span>}</span>
                  <span className="text-sm font-bold text-cyan-700">{v.percentage}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full pm-gradient rounded-full" style={{ width: `${v.percentage}%` }} />
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                  <span>{v.case_count} cases</span><span>{v.length} steps</span><span>~{fmtHours(v.avg_duration_hours)}</span>
                </div>
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-7 p-6">
          <div className="flex items-center gap-2 mb-4"><GitCompare className="w-4 h-4 text-cyan-600" /><h3 className="font-bold text-slate-900">Path comparison</h3></div>
          {selected && (
            <div className="space-y-6">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Reference (Happy Path) · {data[0].percentage}%</div>
                <Flow activities={happy} />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-cyan-700 mb-2">Selected Variant · {selected.percentage}% · {selected.case_count} cases</div>
                <Flow activities={selected.activities} base={happy} />
                {selected.id !== data[0].id && (
                  <p className="text-xs text-slate-500 mt-3">
                    <span className="text-amber-600 font-semibold">Amber</span> steps are deviations from the reference path.
                    This variant runs {selected.avg_duration_hours > data[0].avg_duration_hours ? "slower" : "faster"} ({fmtHours(selected.avg_duration_hours)} vs {fmtHours(data[0].avg_duration_hours)}).
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2">
                {[["Steps", selected.length], ["Cases", selected.case_count], ["Avg Duration", fmtHours(selected.avg_duration_hours)]].map(([l, v]) => (
                  <div key={l} className="p-3 rounded-xl bg-white/60 border border-white/60 text-center">
                    <div className="text-xl font-bold text-slate-900 font-display">{v}</div>
                    <div className="text-xs text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

export default function Variants() { return <NeedTenant><Inner /></NeedTenant>; }
